import { useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import moment from 'moment';
import { MdQrCode } from 'react-icons/md';
import DefaultScreen from '../../DefaultScreen';
import { Title } from '../../../components/managment/Title';
import { logout } from '../../../helpers/Api/PulseemReactAPI';
import {
    activateAuthenticator,
    getAuthenticatorStatus,
    startAuthenticatorSetup,
    turnOffAuthenticator,
} from '../../../redux/reducers/AuthenticatorSlice';

/**
 * Settings > Authenticator app: the logged-in user (main login or SubUser) turns the authenticator app on/off
 * for their own login. At login (web site, Login.aspx) the code from the app is then asked instead of the SMS/email code.
 * API: WebSiteAPI Controllers/Settings/AuthenticatorController.cs
 */
type Step = 'status' | 'scan' | 'codes';

const box: React.CSSProperties = { border: '1px solid var(--pls-d-border, #ddd)', borderRadius: 8, padding: '14px 18px', maxWidth: 760, marginTop: 16, background: 'var(--pls-d-surface, #fff)' };
const helpBox: React.CSSProperties = { background: 'var(--pls-d-surface-2, #f4f8fc)', border: '1px solid var(--pls-d-border, #c9dcef)', borderRadius: 8, padding: '12px 18px', maxWidth: 760, lineHeight: 1.6 };
const codeInput: React.CSSProperties = { direction: 'ltr', letterSpacing: 3, textAlign: 'center' };

const AuthenticatorApp = ({ classes }: any) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isRTL, windowSize } = useSelector((state: any) => state.core);

    const [status, setStatus] = useState<any>(null);
    const [step, setStep] = useState<Step>('status');
    const [setup, setSetup] = useState<any>(null);
    const [code, setCode] = useState('');
    const [turnOffCode, setTurnOffCode] = useState('');
    const [switchingToSms, setSwitchingToSms] = useState(false);
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);

    const tr = (key: string, options?: any) => t(`settings.authenticatorApp.${key}`, options) as string;

    const errorText = (payload: any) => {
        switch (payload?.Message) {
            case 'WRONG_CODE': return tr('errors.wrongCode');
            case 'CODE_ALREADY_USED': return tr('errors.codeUsed');
            case 'ALREADY_ON': return tr('errors.alreadyOn');
            case 'SETUP_EXPIRED': return tr('errors.setupExpired');
            case 'TOO_MANY_TRIES': return tr('errors.tooMany');
            case 'NOT_ON': return tr('errors.notOn');
            default: return tr('errors.general');
        }
    };

    /** Calls the API; returns the payload when StatusCode is 201, otherwise shows the error and returns null.
     *  keepError: don't clear the error shown by the previous call (status refresh right after a failed action). */
    const call = async (action: any, keepError = false) => {
        setBusy(true);
        if (!keepError) setError('');
        try {
            const response = (await dispatch(action)) as any;
            const payload = response?.payload;
            if (payload?.StatusCode === 401) {
                logout();
                return null;
            }
            if (payload?.StatusCode !== 201) {
                setError(errorText(payload));
                return null;
            }
            return payload;
        } finally {
            setBusy(false);
        }
    };

    const loadStatus = async (keepError = false) => {
        const payload = await call(getAuthenticatorStatus(), keepError);
        if (payload) setStatus(payload.Data);
    };

    useEffect(() => {
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onStartSetup = async () => {
        setMessage('');
        const payload = await call(startAuthenticatorSetup());
        if (payload) {
            setSetup(payload.Data);
            setCode('');
            setStep('scan');
        } else {
            loadStatus(true);
        }
    };

    const onConfirm = async () => {
        const payload = await call(activateAuthenticator(code.trim()));
        if (payload) {
            setBackupCodes(payload.Data?.BackupCodes || []);
            setSetup(null);
            setStep('codes');
        } else {
            setCode('');
        }
    };

    const onDone = async () => {
        setBackupCodes([]);
        setStep('status');
        setMessage(tr('msgDone'));
        await loadStatus();
    };

    /** Switch back to SMS / email = turn the app off. Needs the current app code (or a backup code) to prove it is the user. */
    const onSwitchToSms = async () => {
        setMessage('');
        const payload = await call(turnOffAuthenticator(turnOffCode.trim()));
        setTurnOffCode('');
        if (payload) {
            setSwitchingToSms(false);
            setMessage(tr('msgOff'));
            await loadStatus();
        }
    };

    /** The "How do you get your login code?" choice. */
    const onMethodChange = (method: string) => {
        setError('');
        setMessage('');
        if (method === 'app') {
            if (status?.IsOn) setSwitchingToSms(false); // changed their mind - stay on the app
            else onStartSetup();                        // app chosen -> setup (QR, first code, backup codes)
        } else if (status?.IsOn) {
            setTurnOffCode('');
            setSwitchingToSms(true);                    // SMS / email chosen -> confirm with the current app code
        }
    };

    const downloadCodes = () => {
        const text = `${tr('downloadHeader')}\r\n\r\n${backupCodes.join('\r\n')}`;
        const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pulseem-backup-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const button = (text: string, onClick: () => void, disabled = false) => (
        <Button
            className={clsx(classes.btn, classes.btnRounded)}
            style={{ marginInlineEnd: 10, marginTop: windowSize === 'xs' ? 10 : 0 }}
            disabled={busy || disabled}
            onClick={onClick}
        >
            {text}
        </Button>
    );

    const methodOption = (value: string, title: string, description: string) => (
        <FormControlLabel
            value={value}
            disabled={busy}
            control={<Radio color="primary" />}
            style={{ alignItems: 'flex-start', margin: '6px 0' }}
            label={
                <Box style={{ paddingTop: 9 }}>
                    <Typography style={{ fontWeight: 'bold' }}>{title}</Typography>
                    <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 14 }}>{description}</Typography>
                </Box>
            }
        />
    );

    const renderStatus = () => {
        if (!status) return null;
        // the radio shows the chosen method; while confirming the switch it already shows SMS / email
        const method = status.IsOn && !switchingToSms ? 'app' : 'sms';
        return (
            <>
                <Box style={box}>
                    <Typography variant="h6">{tr('methodTitle')}</Typography>
                    <RadioGroup value={method} onChange={(e: any) => onMethodChange(e.target.value)}>
                        {methodOption('sms', tr('methodSms'), tr('methodSmsDesc'))}
                        {methodOption('app', tr('methodApp'), tr('methodAppDesc'))}
                    </RadioGroup>

                    {status.IsOn && !switchingToSms && (
                        <Typography style={{ marginTop: 8 }}>
                            {tr('statusLabel')} <b style={{ color: 'var(--pls-d-success, #1b7f3b)' }}>{tr('on')}</b>{' '}
                            {tr('since', { date: moment(status.OnSince).format('DD/MM/YYYY') })}.{' '}
                            {tr('codesLeft', { count: status.BackupCodesLeft })}
                        </Typography>
                    )}
                    {!status.IsOn && <Typography style={{ marginTop: 8, color: 'var(--pls-d-text-2, #555)' }}>{tr('setupHint')}</Typography>}
                </Box>

                {switchingToSms && (
                    <Box style={box}>
                        <Typography style={{ fontWeight: 'bold' }}>{tr('switchTitle')}</Typography>
                        <Typography style={{ margin: '6px 0 12px 0' }}>{tr('switchText')}</Typography>
                        <Box style={{ display: windowSize !== 'xs' ? 'flex' : 'block', alignItems: 'center' }}>
                            <TextField
                                variant="outlined"
                                size="small"
                                value={turnOffCode}
                                autoFocus
                                placeholder={tr('codeOrBackupPlaceholder')}
                                inputProps={{ maxLength: 11, style: codeInput, autoComplete: 'off' }}
                                onChange={(e) => setTurnOffCode(e.target.value.toUpperCase().replace(/[^0-9A-Z-]/g, ''))}
                                onKeyDown={(e: any) => { if (e.key === 'Enter' && turnOffCode.trim() !== '') onSwitchToSms(); }}
                                style={{ marginInlineEnd: 10, width: 200 }}
                            />
                            {button(tr('switchButton'), onSwitchToSms, turnOffCode.trim() === '')}
                            {button(tr('cancel'), () => { setSwitchingToSms(false); setError(''); })}
                        </Box>
                    </Box>
                )}

                {status.IsOn && (
                    <>
                        <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 13, marginTop: 10 }}>{tr('newPhoneHint')}</Typography>
                        <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 13, marginTop: 4 }}>{tr('lostPhone')}</Typography>
                    </>
                )}
            </>
        );
    };

    const renderScan = () => (
        <Box style={box}>
            <Typography variant="h6">{tr('step1Title')}</Typography>
            <Typography style={{ marginBottom: 12 }}>{tr('step1Text')}</Typography>

            <Typography variant="h6">{tr('step2Title')}</Typography>
            <Typography>{tr('step2Text', { account: setup?.AccountName })}</Typography>
            {setup?.QrImage && <img src={setup.QrImage} alt="QR code" style={{ width: 220, height: 220, margin: '10px 0', border: '1px solid var(--pls-d-border, #ddd)', imageRendering: 'pixelated' }} />}
            <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 13 }}>{tr('cantScan')}</Typography>
            <Typography style={{ fontFamily: 'Consolas, monospace', fontSize: 16, direction: 'ltr', display: 'inline-block', background: 'var(--pls-d-surface-2, #f3f3f3)', padding: '2px 8px', margin: '4px 0 14px 0' }}>
                {setup?.SetupKey}
            </Typography>

            <Typography variant="h6">{tr('step3Title')}</Typography>
            <Box style={{ display: windowSize !== 'xs' ? 'flex' : 'block', alignItems: 'center', marginTop: 6 }}>
                <TextField
                    variant="outlined"
                    size="small"
                    value={code}
                    placeholder={tr('codePlaceholder')}
                    inputProps={{ maxLength: 6, inputMode: 'numeric', style: { ...codeInput, fontSize: 20 }, autoComplete: 'one-time-code' }}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e: any) => { if (e.key === 'Enter' && code.length === 6) onConfirm(); }}
                    style={{ marginInlineEnd: 10, width: 160 }}
                />
                {button(tr('confirm'), onConfirm, code.length !== 6)}
                {button(tr('cancel'), () => { setStep('status'); setSetup(null); setError(''); })}
            </Box>
        </Box>
    );

    const renderCodes = () => (
        <Box style={box}>
            <Typography variant="h6">{tr('onTitle')}</Typography>
            <Box style={{ background: 'var(--pls-d-warning-bg, #fff6e0)', border: '1px solid var(--pls-d-warning, #e8c66a)', padding: '8px 12px', margin: '10px 0', borderRadius: 6 }}>
                <Typography style={{ fontWeight: 'bold' }}>{tr('saveCodesTitle')}</Typography>
                <Typography>{tr('saveCodesText')}</Typography>
            </Box>
            <Box style={{ fontFamily: 'Consolas, monospace', fontSize: 16, direction: 'ltr', background: 'var(--pls-d-surface-2, #f7f7f7)', border: '1px solid var(--pls-d-border, #ddd)', padding: '10px 16px', columns: 2, width: 320 }}>
                {backupCodes.map((c) => <div key={c}>{c}</div>)}
            </Box>
            <Box style={{ marginTop: 14 }}>
                {button(tr('download'), downloadCodes)}
                {button(tr('done'), onDone)}
            </Box>
        </Box>
    );

    return (
        <DefaultScreen
            currentPage="settings"
            subPage="authenticatorApp"
            key="authenticatorApp"
            classes={classes}
            containerClass={clsx(classes.management, classes.mb50)}
        >
            <Box className={'topSection'}>
                <Box className={clsx('head')}>
                    <Title
                        classes={classes}
                        isIcon={false}
                        ContainerStyle={{ padding: windowSize === 'xs' ? '10px 15px' : '11px 31.69px 8px 31.69px' }}
                        Element={
                            <Box className={clsx(classes.dFlex)} alignItems="center">
                                <MdQrCode size={26} style={{ marginInlineEnd: 10 }} />
                                <Typography className={clsx(classes.managementTitle, 'mgmtTitle')} style={{ width: 'auto' }}>{tr('title')}</Typography>
                            </Box>
                        }
                    />
                </Box>
            </Box>
            <Box style={{ padding: windowSize === 'xs' ? '10px 15px' : '10px 32px', direction: isRTL ? 'rtl' : 'ltr' }}>
                <Box style={helpBox}>
                    <Typography>{tr('help')}</Typography>
                </Box>
                {message && <Typography style={{ color: 'var(--pls-d-success, #1b7f3b)', fontWeight: 'bold', marginTop: 12 }}>{message}</Typography>}
                {error && <Typography style={{ color: 'var(--pls-d-danger, #c00)', fontWeight: 'bold', marginTop: 12 }}>{error}</Typography>}
                {step === 'status' && renderStatus()}
                {step === 'scan' && renderScan()}
                {step === 'codes' && renderCodes()}
            </Box>
        </DefaultScreen>
    );
};

export default AuthenticatorApp;
